// services
import StatsService from '@services/statsService'; 

// types
import { IExchange } from '@/types/exchanges';

// types
import { Props } from '../../../types';

interface IData {
    keys: string[];
    fetchData: (params: IFetchData) => Promise<any>;
    resolution: { [key: string]: string | number };
}

interface IFetchData {
    symbol: string;
    interval: string;
    from: { from: number; to: number };
    limit?: string | number;
    url?: string;
    resolution?: { [key: string]: string | number };
    start?: number;
    exchangeId?: number | string;
    postFixSymbol?: string,
}

const defaultResolution = {
    '1': '1m',
    '3': '3m',
    '5': '5m',
    '15': '15m',
    '30': '30m',
    '45': '45m',
    '60': '1h',
    '120': '2h',
    '180': '3h',
    '240': '4h',
    '1D': '1d',
    '1W': '1w',
    '1M': '1M',
};

// binance
const fetcWsKline = async ({
    symbol,
    interval,
    from,
    exchangeId,
    resolution = {},
}: IFetchData) => {
    // Проверка, чтобы startTime и endTime были в миллисекундах
    const start = String(from.from > 1e12 ? from.from : from.from * 1000);

    // convert resolution
    interval = String(resolution[interval] || '15m');

    try {
        const response = await StatsService.getKlineData({
            symbol,
            interval,
            // start,
            exchange: exchangeId,
        });
        if (!response.status) {
            throw new Error('Ошибка при получении данных с WsKline');
        }
        
        const prepare = response.data.data.records.map((item: any) => ({
            time: Number(item.ts) * 1000,
            open: parseFloat(item.open),
            high: parseFloat(item.high),
            low: parseFloat(item.low),
            close: parseFloat(item.close),
        }));

        return prepare;
    } catch (error) {
        console.error('Ошибка при получении данных с WsKline:', error);
        return [];
    }
};

// binance
const fetchDataBinance = async ({
    symbol,
    interval,
    from,
    limit = 1000,
    url = 'https://api.binance.com/api/v1/klines',
    resolution = {},
    postFixSymbol,
}: IFetchData) => {
    // Проверка, чтобы startTime и endTime были в миллисекундах
    const startTime = String(from.from > 1e12 ? from.from : from.from * 1000);
    const endTime = String(from.to > 1e12 ? from.to : from.to * 1000);

    // convert resolution
    interval = String(resolution[interval] || '15m');

    const params = new URLSearchParams({
        symbol: symbol + postFixSymbol,
        interval,
        startTime,
        endTime,
        limit: String(limit), // Максимум 1000 свечей
    });

    try {
        const response = await fetch(`${url}?${params}`);
        if (!response.ok) {
            throw new Error('Ошибка при получении данных с Binance');
        }
        const data = await response.json();
        return data.map((item: any) => ({
            time: item[0], // Время открытия свечи
            open: parseFloat(item[1]), // Открытие
            high: parseFloat(item[2]), // Высокая цена
            low: parseFloat(item[3]), // Низкая цена
            close: parseFloat(item[4]), // Закрытие
            volume: parseFloat(item[5]), // Объем
        }));
    } catch (error) {
        console.error('Ошибка при получении данных с Binance:', error);
        return [];
    }
};

// binance
const fetchDataBybit = async ({
    symbol,
    interval,
    from,
    limit = 1000,
    url = 'https://api.bybit.com/v5/market/kline?category=spot',
    resolution = {},
}: IFetchData) => {
    // Проверка, чтобы startTime и endTime были в миллисекундах
    const start = String(from.from > 1e12 ? from.from : from.from * 1000);
    const end = String(from.to > 1e12 ? from.to : from.to * 1000);

    // convert resolution
    interval = String(resolution[interval] || '15');

    const params = new URLSearchParams({
        symbol,
        interval,
        start,
        end,
        limit: String(limit),
    });

    try {
        const response = await fetch(`${url}&${params}`);
        if (!response.ok) {
            throw new Error('Ошибка при получении данных с ByBit');
        }
        const data = await response.json();

        return data.result.list.map((item: any) => {
            return {
                time: Number(item[0]),
                open: parseFloat(item[1]),
                high: parseFloat(item[2]),
                low: parseFloat(item[3]),
                close: parseFloat(item[4]),
                volume: parseFloat(item[5]),
            };
        }).sort((a: any, b: any) => a.time - b.time);;
    } catch (error) {
        console.error('Ошибка при получении данных с Binance:', error);
        return [];
    }
};

const urls: { [key: string]: string } = {
    binance: 'https://api.binance.com/api/v1/klines',
    binance_f: 'https://fapi.binance.com/fapi/v1/klines',
    binance_fc: 'https://dapi.binance.com/dapi/v1/klines',
    bybit: 'https://api.bybit.com/v5/market/kline?category=spot',
    bybit_f: 'https://api.bybit.com/v5/market/kline?category=linear',
};

const postFixSymbol: { [key: string]: string } = {
    binance: '',
    binance_f: '',
    binance_fc: '_PERP ',
    bybit: '',
    bybit_f: '',
};

const data: IData[] = [
    {
        keys: ['binance', 'binance_fc', 'binance_tf', 'binance_t'],
        fetchData: fetchDataBinance,
        resolution: defaultResolution,
    }, {
        keys: ['bybit', 'bybit_f'],
        fetchData: fetchDataBybit,
        resolution: {
            '1m': '1',
            '3m': '3',
            '5m': '5',
            '15m': '15',
            '30m': '30',
            '45m': '45',
            '1h': '60',
            '2h': '120',
            '3h': '180',
            '4h': '240',
            '1d': '1D',
            '1w': '1W',
            '1M': '1M',
        },        
    },
];

export const useExchanges = (props: Props) => {
    const getFetch = (exchange: IExchange): ((params: IFetchData) => Promise<any>) => {
        if (exchange.kline_remote) {
            const item = data.find((el: IData) => el.keys.includes(exchange.code_name));
            return item
                ? (params) => item.fetchData(
                    {
                        ...params,
                        url: urls[exchange.code_name],
                        resolution: item.resolution,
                        postFixSymbol: postFixSymbol[exchange.code_name],
                    },
                )
                : (params) => fetchDataBinance(
                    {
                        ...params,
                        url: urls[exchange.code_name],
                        resolution: defaultResolution,
                        postFixSymbol: postFixSymbol[exchange.code_name],
                    },
                );
        } else if (exchange.ws_kline) {
            const symbol = props.data.pair.toUpperCase().replace('/', '-');
            return (params) => fetcWsKline({ ...params, symbol, exchangeId: exchange.id, resolution: defaultResolution });
        }
    
        return (params) => fetchDataBinance({ ...params, url: urls[exchange.code_name], resolution: defaultResolution });
    };

    return {
        getFetch,
    };
};
